import { Atom, PrimitiveAtom, atom } from "jotai";
import { createStorableCellAtom } from "./cell";
import { createItemizationAtom } from "./itemization";
import { CellTypes, DollarCell, ItemizationLineItem } from "./types";
import {
  netWorkingCapitalMolecule,
  workingCapitalEpochsAtomsContainer,
  workingCapitalFormulaIdAtom,
} from "./workingCapital";

const createCalculatedFormulaCellAtom = (
  molecule: Atom<CellTypes>[],
  options?: { isNegative?: boolean },
) => {
  return atom((get) => {
    const formulaId = get(workingCapitalFormulaIdAtom);
    const nwcAtoms = get(netWorkingCapitalMolecule);
    const nwcValues = nwcAtoms
      .slice(1)
      .map((atom) => Number(get(atom).value) || 0);

    const cells = molecule.map((cell) => get(cell));
    const values = cells.map((cell) => Number(cell.value || 0));

    let value: number;

    if (formulaId === "average") {
      const sum = values.reduce((acc, value) => acc + value, 0);
      value = sum / values.length;
    } else if (formulaId === "ttm") {
      const sum = values.slice(-12).reduce((acc, value) => acc + value, 0);
      value = sum / Math.min(values.length, 12);
    } else {
      let index = values.length - 1;
      if (formulaId === "max") {
        index = nwcValues.lastIndexOf(Math.max(...nwcValues));
      } else if (formulaId === "min") {
        index = nwcValues.lastIndexOf(Math.min(...nwcValues));
      }

      value = values[index];
    }

    return {
      type: "dollar",
      value,
      isLocked: true,
      isNegative: !!options?.isNegative,
    } as DollarCell;
  });
};

export const formulaRangeAtom = atom((get) => {
  const formulaId = get(workingCapitalFormulaIdAtom);
  const orderedEpochs = get(
    workingCapitalEpochsAtomsContainer.orderedEpochsMolecule,
  );

  if (formulaId === "average") {
    if (orderedEpochs.length < 1) return [];

    const start = get(orderedEpochs[0]).date;
    const end = get(orderedEpochs[orderedEpochs.length - 1]).date;

    return [start, end];
  }

  if (formulaId === "ttm") {
    const items = orderedEpochs.slice(-12);
    if (items.length < 1) return [];

    const start = get(items[0]).date;
    const end = get(items[items.length - 1]).date;

    return [start, end];
  }

  const nwcAtoms = get(netWorkingCapitalMolecule);
  const nwcValues = nwcAtoms
    .slice(1)
    .map((atom) => Number(get(atom).value) || 0);

  let index = nwcValues.length - 1;
  if (formulaId === "max") {
    index = nwcValues.lastIndexOf(Math.max(...nwcValues));
  } else if (formulaId === "min") {
    index = nwcValues.lastIndexOf(Math.min(...nwcValues));
  }

  const item = orderedEpochs[index];
  if (!item) return [];
  const date = get(item).date;

  return [date];
});

export const createStorableEpochMolecule = (
  id: string,
  options?: { isNegative?: boolean },
) => {
  return atom<PrimitiveAtom<CellTypes>[]>((get) => {
    const orderedEpochs = get(
      workingCapitalEpochsAtomsContainer.orderedEpochsMolecule,
    );

    const cells = orderedEpochs.map((epochAtom) => {
      const epoch = get(epochAtom);

      return createStorableCellAtom(`workingcapital|${id}|${epoch.id}`, {
        type: "dollar",
        value: 0,
        isNegative: options?.isNegative || false,
      } satisfies DollarCell) as PrimitiveAtom<CellTypes>;
    });

    return [...cells];
  });
};

const createStorableFromulaRowMolecule = (
  id: string,
  options?: { isNegative?: boolean },
) => {
  return atom<PrimitiveAtom<CellTypes>[]>((get) => {
    const cells = get(createStorableEpochMolecule(id, options));

    const formulaAtom = createCalculatedFormulaCellAtom(
      cells,
      options,
    ) as PrimitiveAtom<CellTypes>;

    return [formulaAtom, ...cells];
  });
};

export const createComputedFormulaRowMolecule = (
  fn: (index: number, prevAtoms: Atom<CellTypes>[]) => Atom<CellTypes>,
) => {
  const prevAtoms: Atom<CellTypes>[] = [];
  const cache: Record<number, Atom<CellTypes>> = {};

  return atom((get) => {
    const epochAtoms = get(
      workingCapitalEpochsAtomsContainer.orderedEpochsMolecule,
    );

    const count = epochAtoms.length + 1;

    const molecule = Array.from({ length: count }, (_, i) => {
      if (cache[i]) return cache[i];
      const newAtom = fn(i, prevAtoms);
      prevAtoms.push(newAtom);
      cache[i] = newAtom;
      return newAtom;
    });

    return [...molecule];
  });
};

export const createAggregatedFormulaItemization = (
  id: string,
  options?: {
    isNegative?: boolean;
    staticLineItems?: ItemizationLineItem[];
  },
) => {
  return createItemizationAtom({
    id,
    consolidatedMolecule: createStorableFromulaRowMolecule(
      id + "|consolidated",
      options,
    ),
    lineItemToMolecule: (lineItem) =>
      createStorableFromulaRowMolecule(`${id}|lineItem|${lineItem.id}`, {
        isNegative: options?.isNegative,
      }),
    lineItemColumnToCellAtom: (cells) =>
      atom((get) => {
        const values = cells.map((cell) => Number(get(cell).value || 0));
        const sum = values.reduce((acc, value) => acc + value, 0);

        return {
          type: "dollar",
          value: sum || 0,
          isNegative: options?.isNegative || false,
          isLocked: true,
        } as DollarCell;
      }),
    staticLineItems: options?.staticLineItems || [],
  });
};
